// Trigger helpers for `activateWhen` (iteration 13), exported from @raventic/sdk.
//
//   activateWhen: onDataLayer(ga4Event("view_item"))                   // once, replayed
//   activateWhen: onDataLayer(ga4Event("add_to_cart"), { once: false }) // every event
//   activateWhen: onElement(".cart-lightbox")                           // once, 30 s max
//
// Each helper builds an ActivationTrigger; the facade's runtime
// (./activation-runtime.ts) subscribes it after boot and draws the widget on every
// fire, with the fired data on `ctx.trigger`.
//
// The dataLayer hook is OUR OWN, deliberately not analytics' collector: that one
// polls the layer by index every ~5 s, far too slow for UI. Instead one shared hub
// per layer name wraps the array's `push` (calling the original first) so an entry
// is seen the moment it lands, and a 500 ms self-heal repairs the hook when a third
// party breaks it (some platforms reset `push` or reassign the whole array).
//
// Hard rules, because this code runs inside the HOST's `push`:
//   - nothing may throw out of our wrapper: it always returns the original push's
//     return value; every listener / matcher / normalize call is isolated;
//   - no wrapper ping-pong: the hub registry lives on `window` (`__rvnDL`, keyed by
//     layer), so two bundle copies on one page share one hub, and the self-heal
//     re-wraps ONLY when the array identity changed or entries were demonstrably
//     missed (the heal's own drain delivered something our wrapper never saw). A
//     third-party wrapper on top of ours that chains down to it (GTM) is left alone.
//
// Delivery: an index cursor, so each push is delivered once — and the SAME object
// pushed twice fires twice. A WeakSet of delivered objects is consulted ONLY when
// the hub has to rescan an array from 0 (a reassigned array that copied the old
// entries over, or a front-trimmed / capped array), so copied entries are not
// re-delivered. Known limits: an entry pushed to the OLD array after a reassignment
// (a stale reference) is missed; a primitive entry (not an object) re-delivered by a
// rescan cannot be deduplicated. Real dataLayer entries are objects.

import type { ActivationContext, ActivationTrigger } from "./activation";

// A dataLayer entry matcher. ga4Event()'s matchers also carry `normalize`, applied to
// the matched entry before it is fired / returned (see ga4Event).
export interface DataLayerMatcher {
  (entry: unknown): boolean;
  normalize?: (entry: unknown) => unknown;
}

export interface OnDataLayerOptions {
  // Fire once, then stop listening. Default true.
  once?: boolean;
  // Also match entries pushed BEFORE subscribe. Default = `once` (a one-shot PDP
  // event may well precede widget.js; a repeating add-to-cart must not redraw for
  // an old cart action). With `once`, the LATEST matching earlier entry fires.
  replay?: boolean;
  // window[layer]. Default "dataLayer".
  layer?: string;
  // Stop listening after this many ms. Default: never (the hook is cheap).
  // Non-finite (e.g. Infinity) = never; clamped to 2^31-1.
  timeout?: number;
}

export interface OnElementOptions {
  // Fire for the first matching element only, then stop. Default true.
  once?: boolean;
  // Stop observing after this many ms. Default 30 000 when `once`, never otherwise:
  // a REPEATING element trigger observes the document for the page's lifetime —
  // prefer onDataLayer for repeat cases. Non-finite = never; clamped to 2^31-1.
  timeout?: number;
}

// What ga4Event() normalizes a matched entry to — both push styles alike, so getters
// read `(ctx.trigger as Ga4EventData).ecommerce?.items` either way.
export interface Ga4EventData {
  event: string;
  ecommerce?: Ga4Ecommerce;
}
export interface Ga4Ecommerce {
  items?: Array<{ item_id?: string; item_name?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

const DEFAULT_LAYER = "dataLayer";
const HEAL_INTERVAL = 500;
const DEFAULT_ELEMENT_TIMEOUT = 30_000;
const MAX_TIMEOUT = 2_147_483_647; // setTimeout's 32-bit ceiling (larger fires at once)
const REGISTRY = "__rvnDL";
// Marks every wrapper we install (any bundle copy): identifies ours in the push chain.
export const WRAPPER_MARK = Symbol.for("raventic.dl.wrapper");

// undefined = no timeout (absent or non-finite); else clamped into setTimeout's range.
export function normalizeTimeout(ms: number | undefined): number | undefined {
  if (ms === undefined || !Number.isFinite(ms)) return undefined;
  return Math.min(Math.max(ms, 0), MAX_TIMEOUT);
}

const isObject = (v: unknown): v is object => typeof v === "object" && v !== null;

// Run a customer matcher, isolating its exceptions to that one entry.
function safeMatch(match: DataLayerMatcher, entry: unknown): boolean {
  try {
    return Boolean(match(entry));
  } catch (error) {
    console.error("[@raventic/sdk] dataLayer matcher threw (entry skipped)", error);
    return false;
  }
}

// The value to fire / return for a matched entry. A throwing normalize skips the
// entry (logged): `ok: false`.
function deliverable(match: DataLayerMatcher, entry: unknown): { ok: boolean; value?: unknown } {
  if (!match.normalize) return { ok: true, value: entry };
  try {
    return { ok: true, value: match.normalize(entry) };
  } catch (error) {
    console.error("[@raventic/sdk] dataLayer normalize threw (entry skipped)", error);
    return { ok: false };
  }
}

// ── dataLayer hub ────────────────────────────────────────────────────────────

type PushFn = ((...items: unknown[]) => number) & { [WRAPPER_MARK]?: true };
type LayerArray = unknown[] & { push: PushFn };
type Listener = (entry: unknown) => void;

// Plain data only (no module-bound functions), so every bundle copy can drive it.
interface Hub {
  layer: string;
  arr: LayerArray | null;
  cursor: number;
  // The entry at `cursor - 1` when it was consumed: if it moved, the array was
  // trimmed / rewritten and the hub rescans.
  last: unknown;
  // During a rescan, entries at indices below this are deduped against `seen`.
  dedupeBelow: number;
  wrapper: PushFn | null;
  seen: WeakSet<object>;
  listeners: Set<Listener>;
  heal: ReturnType<typeof setInterval> | null;
}

function hubFor(layer: string): Hub {
  const w = window as unknown as Record<string, unknown>;
  let registry = w[REGISTRY] as Record<string, Hub> | undefined;
  if (!registry) {
    registry = Object.create(null) as Record<string, Hub>;
    Object.defineProperty(w, REGISTRY, { value: registry, enumerable: false, configurable: true });
  }
  let hub = registry[layer];
  if (!hub) {
    hub = {
      layer,
      arr: null,
      cursor: 0,
      last: undefined,
      dedupeBelow: 0,
      wrapper: null,
      seen: new WeakSet(),
      listeners: new Set(),
      heal: null,
    };
    registry[layer] = hub;
  }
  return hub;
}

function wrap(hub: Hub, arr: LayerArray): void {
  const previous = arr.push;
  const wrapper: PushFn = function (this: unknown, ...items: unknown[]): number {
    // The host's push semantics first — its own exceptions stay its own…
    const out = previous.apply(this, items);
    // …ours never escape into it.
    try {
      drain(hub);
    } catch (error) {
      console.error("[@raventic/sdk] dataLayer hook failed", error);
    }
    return out;
  };
  wrapper[WRAPPER_MARK] = true;
  arr.push = wrapper; // may throw (frozen array / non-writable push) — callers catch
  hub.wrapper = wrapper;
}

// (Re-)install / repair the hook: `window[layer] ||= []`; a NEW array resets the
// cursor (rescan with dedupe) and is wrapped. On the SAME array, drain first and
// re-wrap only if that drain delivered entries our wrapper had missed — i.e. our
// wrapper is no longer in the push chain. A wrapper stacked on top that chains to
// ours (GTM, another bundle copy) delivers nothing here and is left alone.
function install(hub: Hub): void {
  const w = window as unknown as Record<string, unknown>;
  if (!w[hub.layer]) w[hub.layer] = [];
  const arr = w[hub.layer] as LayerArray;
  if (arr !== hub.arr) {
    hub.arr = arr;
    hub.cursor = 0;
    hub.last = undefined;
    hub.dedupeBelow = Array.isArray(arr) ? arr.length : 0;
    hub.wrapper = null;
  }
  if (!Array.isArray(arr) || typeof arr.push !== "function") return;
  if (hub.wrapper === null) {
    // Drain even if the wrap throws (frozen array / non-writable push): the cursor
    // must still advance so replay sees the earlier entries. The throw is logged by
    // the caller (heal) and retried on the next tick.
    try {
      wrap(hub, arr);
    } finally {
      drain(hub);
    }
    return;
  }
  const missed = drain(hub);
  if (missed > 0 && arr.push !== hub.wrapper) wrap(hub, arr);
}

// Deliver every entry past the cursor; returns how many were delivered. Re-entrant:
// a listener that pushes is handled by the nested drain (cursor + `last` advance
// BEFORE delivery). Each listener call is isolated.
function drain(hub: Hub): number {
  const arr = hub.arr;
  if (!Array.isArray(arr)) return 0;
  if (hub.cursor > arr.length || (hub.cursor > 0 && arr[hub.cursor - 1] !== hub.last)) {
    // Trimmed at the front (shift/splice capping) or rewritten: rescan from 0,
    // skipping objects already delivered.
    hub.cursor = 0;
    hub.dedupeBelow = arr.length;
  }
  let delivered = 0;
  while (hub.cursor < arr.length) {
    const index = hub.cursor++;
    const entry = arr[index];
    hub.last = entry;
    if (isObject(entry)) {
      if (index < hub.dedupeBelow && hub.seen.has(entry)) continue;
      hub.seen.add(entry);
    }
    delivered++;
    for (const listener of [...hub.listeners]) {
      try {
        listener(entry);
      } catch (error) {
        console.error("[@raventic/sdk] dataLayer listener threw", error);
      }
    }
  }
  if (hub.dedupeBelow > hub.cursor) hub.dedupeBelow = hub.cursor;
  return delivered;
}

function heal(hub: Hub): void {
  try {
    install(hub);
  } catch (error) {
    console.error("[@raventic/sdk] dataLayer self-heal failed", error);
  }
}

function addListener(hub: Hub, listener: Listener): () => void {
  hub.listeners.add(listener);
  if (hub.heal === null) hub.heal = setInterval(() => heal(hub), HEAL_INTERVAL);
  return () => {
    hub.listeners.delete(listener);
    if (hub.listeners.size === 0 && hub.heal !== null) {
      clearInterval(hub.heal);
      hub.heal = null;
    }
  };
}

// ── helpers ──────────────────────────────────────────────────────────────────

// Fires with each dataLayer entry `match` accepts (normalized, for ga4Event matchers).
export function onDataLayer(match: DataLayerMatcher, opts: OnDataLayerOptions = {}): ActivationTrigger {
  const once = opts.once ?? true;
  const replay = opts.replay ?? once;
  const layer = opts.layer ?? DEFAULT_LAYER;
  const timeout = normalizeTimeout(opts.timeout);
  return {
    kind: "trigger",
    once,
    subscribe(_ctx: ActivationContext, fire: (data?: unknown) => void): () => void {
      const hub = hubFor(layer);
      let active = true;
      let removeListener: (() => void) | null = null;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const stop = (): void => {
        active = false;
        removeListener?.();
        removeListener = null;
        if (timer !== null) clearTimeout(timer);
        timer = null;
      };
      const hit = (entry: unknown): void => {
        if (!active) return;
        const d = deliverable(match, entry);
        if (!d.ok) return;
        if (once) stop();
        fire(d.value);
      };

      // Catch the hub up first (delivers pending entries to EXISTING listeners), so
      // everything before the cursor is "pushed before subscribe". A layer we can't
      // hook (frozen array, non-writable push) is logged, never thrown: the heal
      // keeps trying.
      heal(hub);
      const arr = hub.arr;
      if (replay && Array.isArray(arr)) {
        const before = Array.prototype.slice.call(arr, 0, hub.cursor) as unknown[];
        if (once) {
          for (let i = before.length - 1; i >= 0; i--) {
            if (safeMatch(match, before[i])) {
              hit(before[i]);
              if (!active) break;
            }
          }
        } else {
          for (const entry of before) {
            if (!active) break;
            if (safeMatch(match, entry)) hit(entry);
          }
        }
      }
      if (!active) return stop;

      removeListener = addListener(hub, (entry) => {
        if (safeMatch(match, entry)) hit(entry);
      });
      if (timeout !== undefined) timer = setTimeout(stop, timeout);
      return stop;
    },
  };
}

// Matcher for a GA4 event `name`, in both push styles: the GTM object
// `{ event: name, ecommerce, … }` and gtag's `arguments` object
// `["event", name, params]`. Normalizes the fired data to `{ event: name, ecommerce }`
// (gtag: `ecommerce` = the params object, which carries `items`).
export function ga4Event(name: string): DataLayerMatcher {
  const isGtag = (entry: unknown): entry is ArrayLike<unknown> =>
    isObject(entry) &&
    typeof (entry as ArrayLike<unknown>).length === "number" &&
    (entry as ArrayLike<unknown>)[0] === "event" &&
    (entry as ArrayLike<unknown>)[1] === name;
  const matcher: DataLayerMatcher = (entry) =>
    isGtag(entry) || (isObject(entry) && (entry as { event?: unknown }).event === name);
  matcher.normalize = (entry): Ga4EventData => {
    const ecommerce = isGtag(entry) ? entry[2] : (entry as { ecommerce?: unknown }).ecommerce;
    return { event: name, ecommerce: (ecommerce ?? undefined) as Ga4Ecommerce | undefined };
  };
  return matcher;
}

// Fires with each NEWLY appearing element matching `selector` (one already present at
// subscribe counts). Elements are tracked in a WeakSet, so a re-render that keeps the
// same node does not fire again.
export function onElement(selector: string, opts: OnElementOptions = {}): ActivationTrigger {
  const once = opts.once ?? true;
  const timeout =
    opts.timeout !== undefined ? normalizeTimeout(opts.timeout) : once ? DEFAULT_ELEMENT_TIMEOUT : undefined;
  return {
    kind: "trigger",
    once,
    subscribe(ctx: ActivationContext, fire: (data?: unknown) => void): () => void {
      const doc = ctx.document;
      const seen = new WeakSet<Element>();
      let active = true;
      let observer: MutationObserver | null = null;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const stop = (): void => {
        active = false;
        observer?.disconnect();
        observer = null;
        if (timer !== null) clearTimeout(timer);
        timer = null;
      };
      const check = (): void => {
        for (const el of Array.from(doc.querySelectorAll(selector))) {
          if (!active) return;
          if (seen.has(el)) continue;
          seen.add(el);
          if (once) stop();
          fire(el);
        }
      };

      check();
      if (!active) return stop;
      observer = new MutationObserver(check);
      observer.observe(doc.documentElement, { childList: true, subtree: true });
      if (timeout !== undefined) timer = setTimeout(stop, timeout);
      return stop;
    },
  };
}

// Sync lookup of the LATEST dataLayer entry `match` accepts (normalized, for ga4Event
// matchers), or undefined. For id getters when the trigger itself isn't the dataLayer,
// e.g. an onElement lightbox + the product id from the last `add_to_cart`.
export function dataLayerFind(match: DataLayerMatcher, layer: string = DEFAULT_LAYER): unknown {
  const arr = (window as unknown as Record<string, unknown>)[layer];
  if (!Array.isArray(arr)) return undefined;
  for (let i = arr.length - 1; i >= 0; i--) {
    if (safeMatch(match, arr[i])) {
      const d = deliverable(match, arr[i]);
      if (d.ok) return d.value;
    }
  }
  return undefined;
}
